/* ============================================================
   COR · Configure / Size Guide — measurement interaction
   - syncs number input + slider + tape readout
   - cinematic size reveal with letter stagger
   - highlights matching size row in chart
   - injects ?size= param into CTA links
   - bilingual via document.documentElement.classList.cor-lang-he
   - pauses motion on prefers-reduced-motion: reduce
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    var root = document.getElementById('cor-size-guide');
    if (!root) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var input  = root.querySelector('#cor-head-cm');
    var slider = root.querySelector('[data-cor-cfg-slider] input[type="range"]');
    var sliderWrap = root.querySelector('[data-cor-cfg-slider]');
    var stepBtns = root.querySelectorAll('[data-cor-cfg-step]');

    var resultEl    = root.querySelector('[data-cor-cfg-result]');
    var resultBig   = root.querySelector('[data-cor-cfg-result-big]');
    var resultLabel = root.querySelector('[data-cor-cfg-result-label]');
    var resultRange = root.querySelector('[data-cor-cfg-result-range]');
    var resultNote  = root.querySelector('[data-cor-cfg-result-note]');

    var tapeCmEl    = root.querySelector('[data-cor-cfg-tape-cm]');
    var rows        = Array.prototype.slice.call(root.querySelectorAll('.cor-cfg-row'));
    var ctas        = Array.prototype.slice.call(root.querySelectorAll('[data-cor-cfg-cta]'));

    // A11y (WCAG 4.1.3): the recommended size value is rendered only inside the
    // aria-hidden letter-stagger element [data-cor-cfg-result-big]. Mirror the
    // plain value into a visually-hidden, AT-reachable node inside the polite
    // live region so screen readers actually announce the recommended size.
    // The value is a neutral size token (XS/S/M…/< XS/> XXL/S / M) so no
    // bilingual data-cor-en/he attributes are needed.
    var resultValueEl = null;
    if (resultEl) {
      resultValueEl = root.querySelector('[data-cor-cfg-result-value]');
      if (!resultValueEl) {
        resultValueEl = document.createElement('span');
        resultValueEl.className = 'visually-hidden';
        resultValueEl.setAttribute('data-cor-cfg-result-value', '');
        resultEl.appendChild(resultValueEl);
      }
    }
    var resultValueTimer = null;

    var MIN = 50, MAX = 68, STEP = 0.5;

    var TABLE = [
      { min: 52, max: 54, size: 'XS' },
      { min: 55, max: 56, size: 'S'  },
      { min: 57, max: 58, size: 'M'  },
      { min: 59, max: 60, size: 'L'  },
      { min: 61, max: 62, size: 'XL' },
      { min: 63, max: 66, size: 'XXL'}
    ];

    var COPY = {
      labelDefault: { en: 'Recommended size', he: 'המידה המומלצת' },
      labelBetween: { en: 'Between sizes', he: 'בין שתי מידות' },
      labelBelow:   { en: 'Below range',  he: 'קטן מהטווח' },
      labelAbove:   { en: 'Above range',  he: 'גדול מהטווח' },
      noteDefault:  { en: 'Enter your measurement to see your starting size.', he: 'הזינו מידה כדי לראות את המידה המתחילה.' },
      noteBelow:    { en: 'Check youth sizing or contact us before ordering.', he: 'מומלץ לבדוק מידות נוער או לפנות אלינו לפני הזמנה.' },
      noteAbove:    { en: 'Confirm brand availability and shell shape before ordering.', he: 'בדקו זמינות לפי מותג וצורת מעטפת לפני הזמנה.' },
      noteMatch:    { en: 'Confirm on the product page before checkout.', he: 'בדקו את המידה בעמוד המוצר לפני התשלום.' },
      noteBetween:  { en: 'Start smaller, then use the roll test before checkout.', he: 'התחילו במידה הקטנה יותר ובצעו בדיקת תנודה לפני התשלום.' },
      ctaWithSize:  { en: 'Continue to helmets · {SIZE}', he: 'לקסדות במידה {SIZE}' },
      ctaDefault:   { en: 'Continue to helmets', he: 'המשיכו לקסדות' }
    };

    function isHe() {
      return document.documentElement.classList.contains('cor-lang-he');
    }
    function t(key) {
      return COPY[key] ? (isHe() ? COPY[key].he : COPY[key].en) : '';
    }
    function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
    function round(v) { return Math.round(v / STEP) * STEP; }
    function format(v) { return Number.isInteger(v) ? String(v) : v.toFixed(1); }

    function findEntry(cm) {
      for (var i = 0; i < TABLE.length; i++) {
        if (cm >= TABLE[i].min && cm <= TABLE[i].max) return TABLE[i];
      }
      return null;
    }
    function between(cm) {
      var lower = null, upper = null;
      for (var i = 0; i < TABLE.length; i++) {
        if (TABLE[i].max < cm) lower = TABLE[i];
        if (TABLE[i].min > cm && !upper) upper = TABLE[i];
      }
      return { lower: lower, upper: upper };
    }

    function clearChildren(node) {
      while (node && node.firstChild) node.removeChild(node.firstChild);
    }

    function setBigText(text, isReady) {
      if (!resultBig) return;
      clearChildren(resultBig);
      var letters = String(text).split('');
      for (var i = 0; i < letters.length; i++) {
        var span = document.createElement('span');
        span.className = 'cor-cfg-result__letter';
        span.textContent = letters[i] === ' ' ? ' ' : letters[i];
        resultBig.appendChild(span);
      }
      if (reduce) {
        var nodes = resultBig.querySelectorAll('.cor-cfg-result__letter');
        for (var j = 0; j < nodes.length; j++) {
          nodes[j].style.opacity = '1';
          nodes[j].style.transform = 'none';
          nodes[j].style.animation = 'none';
        }
      }
      if (resultEl) {
        if (isReady) resultEl.classList.add('is-ready');
        else resultEl.classList.remove('is-ready');
      }
      // Mirror the plain size value into the AT-reachable node. Debounce the
      // write with a short trailing timeout so dragging the slider / typing
      // (which fire setBigText on every step) only announce the settled value
      // in the polite live region instead of queuing a burst (WCAG 4.1.3).
      if (resultValueEl) {
        var plainValue = String(text);
        if (resultValueTimer) clearTimeout(resultValueTimer);
        resultValueTimer = setTimeout(function () {
          resultValueEl.textContent = plainValue;
        }, 250);
      }
    }

    function clearRowActive() {
      rows.forEach(function (r) {
        r.classList.remove('is-active');
        r.setAttribute('aria-pressed', 'false');
      });
    }

    function syncSliderFill(v) {
      if (!sliderWrap) return;
      var pct = ((v - MIN) / (MAX - MIN)) * 100;
      sliderWrap.style.setProperty('--cfg-slider-fill', pct + '%');
    }

    function updateCta(size) {
      ctas.forEach(function (cta) {
        var base = cta.getAttribute('data-cor-cfg-cta-base') || cta.getAttribute('href') || '';
        var url = base;
        if (size) {
          var sep = base.indexOf('?') > -1 ? '&' : '?';
          url = base + sep + 'size=' + encodeURIComponent(size);
        }
        cta.setAttribute('href', url);
        var label = cta.querySelector('[data-cor-cfg-cta-label]');
        if (label) {
          if (size) {
            label.textContent = t('ctaWithSize').replace('{SIZE}', size);
          } else {
            label.textContent = t('ctaDefault');
          }
        }
      });
    }

    function applyState(cmRaw) {
      var cm = round(Number(cmRaw));

      if (tapeCmEl) {
        tapeCmEl.textContent = (!isNaN(cm) && cm >= 40 && cm <= 80) ? format(cm) : '—';
      }

      if (!cm || isNaN(cm) || cm < MIN || cm > MAX) {
        clearRowActive();
        if (resultLabel) resultLabel.textContent = t('labelDefault');
        setBigText('—', false);
        if (resultRange) resultRange.textContent = '—';
        if (resultNote) resultNote.textContent = t('noteDefault');
        updateCta(null);
        return;
      }

      clearRowActive();

      var entry = findEntry(cm);
      if (entry) {
        var row = null;
        for (var i = 0; i < rows.length; i++) {
          if (rows[i].dataset.size === entry.size) { row = rows[i]; break; }
        }
        if (row) {
          row.classList.add('is-active');
          row.setAttribute('aria-pressed', 'true');
        }

        if (resultLabel) resultLabel.textContent = t('labelDefault');
        setBigText(entry.size + ' · ' + entry.min + '–' + entry.max + 'CM', true);
        if (resultRange) resultRange.textContent = entry.min + ' — ' + entry.max + ' cm';
        if (resultNote) {
          var note = row ? (isHe() ? row.getAttribute('data-note-he') : row.getAttribute('data-note-en')) : '';
          resultNote.textContent = note || t('noteMatch');
        }
        updateCta(entry.size);
        return;
      }

      if (cm < TABLE[0].min) {
        if (resultLabel) resultLabel.textContent = t('labelBelow');
        setBigText('< XS', true);
        if (resultRange) resultRange.textContent = '< ' + TABLE[0].min + ' cm';
        if (resultNote) resultNote.textContent = t('noteBelow');
        updateCta(null);
        return;
      }
      if (cm > TABLE[TABLE.length - 1].max) {
        if (resultLabel) resultLabel.textContent = t('labelAbove');
        setBigText('> XXL', true);
        if (resultRange) resultRange.textContent = '> ' + TABLE[TABLE.length - 1].max + ' cm';
        if (resultNote) resultNote.textContent = t('noteAbove');
        updateCta(null);
        return;
      }

      var b = between(cm);
      var label = (b.lower && b.upper) ? (b.lower.size + ' / ' + b.upper.size) : 'M';
      if (resultLabel) resultLabel.textContent = t('labelBetween');
      setBigText(label, true);
      if (resultRange) resultRange.textContent = format(cm) + ' cm';
      if (resultNote) resultNote.textContent = t('noteBetween');
      updateCta(b.lower ? b.lower.size : null);
    }

    function setValue(v, source) {
      v = clamp(round(v), MIN, MAX);
      if (input && source !== 'input') input.value = format(v);
      if (slider && source !== 'slider') slider.value = String(v);
      syncSliderFill(v);
      applyState(v);
    }

    // ---- bindings ----

    if (input) {
      input.addEventListener('input', function () {
        var v = Number(input.value);
        if (!isNaN(v)) {
          if (slider) slider.value = String(clamp(v, MIN, MAX));
          syncSliderFill(clamp(v, MIN, MAX));
        }
        applyState(v);
      });
      input.addEventListener('change', function () {
        setValue(Number(input.value), 'input');
      });
      input.addEventListener('blur', function () {
        setValue(Number(input.value), 'input');
      });
    }

    if (slider) {
      slider.addEventListener('input', function () {
        setValue(Number(slider.value), 'slider');
      });
    }

    Array.prototype.forEach.call(stepBtns, function (btn) {
      btn.addEventListener('click', function () {
        var delta = Number(btn.getAttribute('data-cor-cfg-step') || '0');
        var current = Number(input && input.value);
        if (isNaN(current)) current = slider ? Number(slider.value) : 58;
        if (isNaN(current)) current = 58;
        setValue(current + delta);
      });
    });

    rows.forEach(function (row) {
      row.addEventListener('click', function () {
        var mid = (Number(row.dataset.min) + Number(row.dataset.max)) / 2;
        setValue(mid);
        if (window.innerWidth < 720) {
          var panel = document.getElementById('cor-cfg-measure');
          if (panel) panel.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });

    // initial state
    syncSliderFill(slider ? Number(slider.value) : 58);
    applyState(NaN);

    // bilingual reapply
    var langObserver = new MutationObserver(function () {
      var cur = input && input.value ? Number(input.value) : NaN;
      applyState(cur);
    });
    langObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  });
})();
